import { HttpStatus } from './http-status.util';
import { normalizeApiErrors } from './api-errors.util';

/**
 * Failure codes `POST /api/auth/login` sends in `errorCode`.
 *
 * `errorCode` is the contract; the `errors` prose is not (it varies by
 * environment). Only `InvalidCredentials` counts towards the backend's login
 * rate limit; `ValidationFailed` and `PendingChanges` do not.
 */
export const LoginErrorCode = {
  /** 400: body missing or invalid (e.g. malformed email, missing password). */
  ValidationFailed: 'LOGIN_VALIDATION_FAILED',
  /**
   * 401: unknown email OR wrong password — deliberately not told apart, so the
   * login screen cannot be used to discover which emails have accounts.
   */
  InvalidCredentials: 'LOGIN_INVALID_CREDENTIALS',
  /** 401: the account has an admin change pending. */
  PendingChanges: 'LOGIN_PENDING_CHANGES',
  /**
   * 429: locked out after too many failed attempts (keyed by IP, or IP +
   * email — a backend setting). The body carries `data.lockoutMinutes`, the
   * CONFIGURED length, which the message uses, so every 429 in one lockout
   * gets the same message (the P.O. confirmed there is no countdown). It also
   * carries `data.retryAfterSeconds`, the time LEFT, which only decides when
   * the Login button is enabled again — it is never displayed.
   */
  TooManyAttempts: 'LOGIN_TOO_MANY_ATTEMPTS',
} as const;

export const INVALID_CREDENTIALS_MESSAGE = 'Invalid email or password. Please try again.';
export const PENDING_CHANGES_MESSAGE = 'User has pending changes';

/**
 * Lockout copy built from the server's `data.lockoutMinutes`, so a change to the
 * backend's `LoginRateLimit:LockoutMinutes` config needs no frontend release.
 * The value is always in minutes; only the singular is special-cased.
 *
 * @example loginTooManyAttemptsMessage(15) // 'Too many attempts. Try again in 15 minutes.'
 */
export function loginTooManyAttemptsMessage(lockoutMinutes: number): string {
  const unit = lockoutMinutes === 1 ? 'minute' : 'minutes';
  return `Too many attempts. Try again in ${lockoutMinutes} ${unit}.`;
}

/**
 * Lockout copy when the 429 carries no usable `data.lockoutMinutes` (an older
 * backend, or a malformed value) — never guess a number.
 *
 * Wording approved by the P.O. (2026-10-05).
 */
export const LOGIN_TOO_MANY_ATTEMPTS_FALLBACK_MESSAGE =
  'Too many attempts. Please try again later.';

/**
 * Shown for `LOGIN_VALIDATION_FAILED` (a 400 about the typed input). The form
 * validates client-side, so this is rare — but the fields are what is wrong,
 * so it must not say "try again later" like the generic fallback does.
 *
 * Wording approved by the P.O. (2026-10-05).
 */
export const LOGIN_VALIDATION_FAILED_MESSAGE =
  'Please check your email and password and try again.';

/**
 * Shown for any failure we do not recognise — including 502/504 from nginx
 * while the app is down, and a request that never reached the server.
 *
 * Wording approved by the P.O. (2026-10-05).
 */
export const LOGIN_UNRECOGNISED_FAILURE_MESSAGE =
  'Unable to sign in right now. Please try again later.';

/**
 * Pre-code `errors` prose, kept only for a backend that predates the codes
 * above (the backend kept the `errors` text unchanged, so this still matches).
 * "User not found" is no longer sent — the backend folds it into
 * `LOGIN_INVALID_CREDENTIALS` — but an older backend may still send it.
 *
 * Developer Note (TODO): delete this map (and its use in `resolveLoginFailure`)
 * once every environment sends `LoginErrorCode`s — matching on prose is exactly
 * what `errorCode` exists to replace.
 */
const LEGACY_LOGIN_ERROR_MESSAGES: ReadonlyMap<string, string> = new Map([
  ['Invalid email or password', INVALID_CREDENTIALS_MESSAGE],
  ['User not found', 'User not found'],
  ['User has pending changes', PENDING_CHANGES_MESSAGE],
]);

/**
 * Reads `data.lockoutMinutes` off a lockout envelope. Returns `null` unless it
 * is a whole number greater than zero, which is all the backend promises.
 */
export function readLockoutMinutes(data: unknown): number | null {
  const lockoutMinutes = (data as { lockoutMinutes?: unknown } | null | undefined)
    ?.lockoutMinutes;
  return typeof lockoutMinutes === 'number' &&
    Number.isInteger(lockoutMinutes) &&
    lockoutMinutes > 0
    ? lockoutMinutes
    : null;
}

/**
 * Reads `data.retryAfterSeconds` off a lockout envelope: the time left until
 * the lockout ends, as of this response. Returns `null` unless it is a finite
 * number greater than zero.
 *
 * Unlike `lockoutMinutes` (the configured length, identical on every 429),
 * this shrinks as the lockout runs — it is what keeps the Login button
 * disabled for exactly the right time after a page refresh.
 */
export function readRetryAfterSeconds(data: unknown): number | null {
  const retryAfterSeconds = (data as { retryAfterSeconds?: unknown } | null | undefined)
    ?.retryAfterSeconds;
  return typeof retryAfterSeconds === 'number' &&
    Number.isFinite(retryAfterSeconds) &&
    retryAfterSeconds > 0
    ? retryAfterSeconds
    : null;
}

/**
 * Longest delay `setTimeout` honours (2^31 - 1 ms, about 24.8 days). A longer
 * delay fires IMMEDIATELY, which would re-enable the button at once.
 */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * How long to keep the Login button disabled after a lockout 429, in
 * milliseconds — or `null` when the response says nothing usable.
 *
 * Prefers `retryAfterSeconds` (exact). Falls back to `lockoutMinutes`, which is
 * exact on the 429 that STARTS a lockout but over-long on a later one (e.g. the
 * first attempt after a page refresh) — the button then stays disabled longer
 * than necessary, never shorter.
 */
export function resolveLockoutDurationMs(data: unknown): number | null {
  const retryAfterSeconds = readRetryAfterSeconds(data);
  const lockoutMinutes = readLockoutMinutes(data);
  const durationMs =
    retryAfterSeconds !== null
      ? retryAfterSeconds * 1000
      : lockoutMinutes !== null
        ? lockoutMinutes * 60_000
        : null;
  return durationMs === null ? null : Math.min(Math.ceil(durationMs), MAX_TIMER_DELAY_MS);
}

/** What the login form should show for a failed attempt. */
export interface LoginFailureView {
  message: string;
  /**
   * Whether to paint the email/password fields as wrong. False when the
   * credentials were never judged — a lockout or a transport failure.
   */
  highlightFields: boolean;
  /**
   * For a lockout: how long to keep the Login button disabled, in ms. Absent
   * for every other failure, and for a lockout whose response carries no
   * usable duration (the button then stays enabled — we never guess).
   */
  lockoutDurationMs?: number;
}

/**
 * Whether a login response is the rate-limit lockout.
 *
 * Checks the status AND the code: a 429 without the code is not the lockout.
 * `AuthService.handleError` converts the 429 into a `status: false` value, so
 * `errorCode` must survive that conversion (and `data` must too, for the
 * lockout length — `AuthService.login()` keeps it).
 */
export function isLoginRateLimited(
  response: { statusCode?: number; errorCode?: string } | null | undefined
): boolean {
  return (
    response?.statusCode === HttpStatus.TooManyRequests &&
    response?.errorCode === LoginErrorCode.TooManyAttempts
  );
}

/**
 * Maps a failed login envelope to the copy the form shows. Every failure gets
 * a message — an unrecognised one gets `LOGIN_UNRECOGNISED_FAILURE_MESSAGE`
 * rather than silence.
 *
 * Resolution order: the lockout, then the stable `errorCode`, then the legacy
 * prose (transition only), then the fallback.
 *
 * Pure and DI-free by contract.
 */
export function resolveLoginFailure(response: {
  statusCode?: number;
  errorCode?: string;
  errors?: unknown;
  data?: unknown;
}): LoginFailureView {
  if (isLoginRateLimited(response)) {
    const lockoutMinutes = readLockoutMinutes(response.data);
    const lockoutDurationMs = resolveLockoutDurationMs(response.data);
    return {
      message:
        lockoutMinutes === null
          ? LOGIN_TOO_MANY_ATTEMPTS_FALLBACK_MESSAGE
          : loginTooManyAttemptsMessage(lockoutMinutes),
      highlightFields: false,
      ...(lockoutDurationMs === null ? {} : { lockoutDurationMs }),
    };
  }

  switch (response.errorCode) {
    case LoginErrorCode.InvalidCredentials:
      return { message: INVALID_CREDENTIALS_MESSAGE, highlightFields: true };
    case LoginErrorCode.PendingChanges:
      return { message: PENDING_CHANGES_MESSAGE, highlightFields: true };
    case LoginErrorCode.ValidationFailed:
      return { message: LOGIN_VALIDATION_FAILED_MESSAGE, highlightFields: true };
  }

  const legacyMessage = normalizeApiErrors(response.errors)
    .map((error) => LEGACY_LOGIN_ERROR_MESSAGES.get(error))
    .find((message) => message !== undefined);
  if (legacyMessage) {
    return { message: legacyMessage, highlightFields: true };
  }

  return { message: LOGIN_UNRECOGNISED_FAILURE_MESSAGE, highlightFields: false };
}
